import { ThemeToggle } from '@/components/theme/ThemeToggle';
import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { t } from '@/utils/i18n';
import { NotificationsMenu } from './NotificationsMenu';
import { useRevenueSummary } from '@/hooks/useRevenueSummary';
import { useVideoNotifications } from '@/hooks/useVideoNotifications';
import { Button } from '@/components/ui/button';
import { ChevronLeft, Code, Search } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { VideoSelectorV2 } from '@/components/shared/VideoSelectorV2';
import { CopyCodeModal } from '@/components/shared/CopyCodeModal';
import { TourChecklist } from '@/components/tour/TourChecklist';

interface HeaderProps {
  title?: string;
  subtitle?: string;
  icon?: React.ReactNode;
  showBackButton?: boolean;
  showEmbedButton?: boolean;
  onEmbedClick?: () => void;
  showVideoSelector?: boolean;
  selectedVideoId?: string;
  onVideoChange?: (videoId: string | null) => void;
  // Modo personalização: breadcrumb "Personalização / <recurso>" + Salvar / Copiar código
  personalizationMode?: boolean;
  personalizationBreadcrumbLabel?: string;
  hasUnsavedChanges?: boolean;
  onSave?: () => void;
  isSaving?: boolean;
  isSaved?: boolean;
  showLogo?: boolean;
  onCreateFolder?: () => void;
  searchQuery?: string;
  onSearchChange?: (value: string) => void;
  searchPlaceholder?: string;
  // Props para o modal de copiar código
  videoId?: string;
  videoName?: string;
  userId?: string;
  haveHeadline?: boolean;
  isHTMLSelected?: boolean;
  headlineHtmlCode?: string;
  headlineShowThroughout?: boolean;
  headlineInitialDelay?: string;
  headlineEndDelay?: string;
}

export function Header({
  title,
  subtitle,
  icon,
  showBackButton = false,
  showEmbedButton = false,
  onEmbedClick,
  showVideoSelector = false,
  selectedVideoId,
  onVideoChange,
  personalizationMode = false,
  personalizationBreadcrumbLabel,
  hasUnsavedChanges = false,
  onSave,
  isSaving = false,
  isSaved = false,
  showLogo = false,
  onCreateFolder,
  searchQuery = '',
  onSearchChange,
  searchPlaceholder = translateInterfaceText("Buscar vídeos…"),
  videoId,
  videoName,
  userId,
  haveHeadline,
  isHTMLSelected,
  headlineHtmlCode,
  headlineShowThroughout,
  headlineInitialDelay,
  headlineEndDelay,
}: HeaderProps) {
  useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [showCopyCodeModal, setShowCopyCodeModal] = useState(false);
  const { recentConversions } = useRevenueSummary();
  const { recentlyReadyVideos } = useVideoNotifications();

  // Modal de Copiar Código — compartilhado por todos os branches abaixo
  const copyCodeModal = videoId ? (
    <CopyCodeModal
      isOpen={showCopyCodeModal}
      onClose={() => setShowCopyCodeModal(false)}
      entityType="video"
      entityId={videoId}
      entityName={videoName || 'Vídeo'}
      userId={userId}
      haveHeadline={haveHeadline}
      isHTMLSelected={isHTMLSelected}
      headlineHtmlCode={headlineHtmlCode}
      headlineShowThroughout={headlineShowThroughout}
      headlineInitialDelay={headlineInitialDelay}
      headlineEndDelay={headlineEndDelay}
      defaultTab="js"
      hideJsTab={false}
    />
  ) : null;

  // ─── Personalization Mode Header ───
  if (personalizationMode) {
    const showCopyCode = isSaved && !hasUnsavedChanges && Boolean(videoId);
    return (
      <header className="desktop-header hidden w-full shrink-0 border-b border-border bg-card lg:block">
        <div className="flex min-h-[72px] w-full items-center justify-between gap-5 px-5 py-3.5">
          <nav aria-label={translateInterfaceText("Localização atual")} className="flex min-w-0 items-center gap-2 text-[13px]">
            <span className="shrink-0 font-medium text-muted-foreground">{translateInterfaceText("Personalização")}</span>
            <span className="text-border" aria-hidden="true">/</span>
            <span className="truncate font-semibold text-foreground">
              {personalizationBreadcrumbLabel || translateInterfaceText("Visão geral")}
            </span>
          </nav>
          <div className="flex shrink-0 items-center gap-3">
            <ThemeToggle />
            {showCopyCode ? (
              <Button
                data-tour="btn-copy-code"
                onClick={() => setShowCopyCodeModal(true)}
                className="gap-2 h-[42px] px-5 rounded-[10px] font-semibold text-sm shadow-[0_2px_8px_rgba(37,99,235,0.18)] transition-colors duration-150"
                size="sm"
              >
                <Code className="h-4 w-4" />
                {translateInterfaceText("Copiar código")}</Button>
            ) : (
              <>
                {hasUnsavedChanges && !isSaving && (
                  <span className="flex items-center gap-1.5 text-[13px] text-muted-foreground">
                    <span className="w-2 h-2 rounded-full bg-amber-500" />
                    {translateInterfaceText("Alterações não salvas")}</span>
                )}
                <Button
                  data-tour="btn-save"
                  onClick={onSave}
                  disabled={isSaving}
                  className="gap-2 h-[42px] px-5 rounded-[10px] font-semibold text-sm shadow-[0_2px_8px_rgba(37,99,235,0.18)] transition-colors duration-150"
                  size="sm"
                >
                  {isSaving ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      {translateInterfaceText("Salvando...")}</>
                  ) : (
                    translateInterfaceText("Salvar Alterações")
                  )}
                </Button>
              </>
            )}
          </div>
        </div>

        {copyCodeModal}
      </header>
    );
  }

  if (title) {
    return (
      <header className="desktop-header hidden lg:flex items-center justify-between bg-background h-[78px] pl-[24px] pr-[20px]">
        <div className="max-w-[1280px] w-full flex items-center justify-between">
        <div className="flex items-center gap-4">
          {showBackButton && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/meus-videos')}
              className="h-8 w-8 mb-0.5"
            >
              <ChevronLeft className="h-5 w-5" />
            </Button>
          )}
          <div className="flex flex-col">
          <div className="flex items-center gap-2">
            {icon}
              <h1 className="text-[22px] font-semibold text-[#111827] leading-none">{title}</h1>
            </div>
            {subtitle && <p className="text-[15px] text-[#6B7280] mt-1 opacity-60">{subtitle}</p>}
          </div>
          
          {showVideoSelector && onVideoChange && (
            <div>
              <VideoSelectorV2
                value={selectedVideoId || ''}
                onChange={onVideoChange}
                placeholder={translateInterfaceText("Selecione um vídeo para personalizar...")}
                allowPin={true}
                showTabs={true}
                allowClear={false}
                disabled={false}
                className="min-w-[280px]"
              />
            </div>
          )}
        </div>
        
        <div className="flex items-center gap-[12px] mr-[6px] flex-shrink-0">
          <ThemeToggle />
          {!location.pathname.startsWith('/personalizations') && <TourChecklist />}
          {/* Badge de alterações não salvas */}
          {hasUnsavedChanges && !isSaving && (
            <div className="flex items-center gap-2 px-3 h-[38px] rounded-xl">
              <div className="w-2 h-2 bg-orange-400 rounded-full animate-pulse" />
              <span className="text-xs font-medium text-gray-400">{translateInterfaceText("Alterações não salvas")}</span>
            </div>
          )}

          {/* Botão Salvar - Aparece quando há mudanças não salvas */}
          {hasUnsavedChanges && onSave && (
            <TooltipProvider delayDuration={300}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    onClick={onSave}
                    disabled={isSaving}
                    className="gap-2 h-[38px] px-[18px] rounded-xl font-semibold text-sm shadow-lg transition-all duration-200 border-0"
                    size="sm"
                    style={{
                      background: 'linear-gradient(135deg, #2D6CF6 0%, #1E5FE6 100%)',
                      color: '#FFFFFF',
                      boxShadow: '0 4px 12px rgba(45, 108, 246, 0.3)'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-1px)';
                      e.currentTarget.style.boxShadow = '0 6px 20px rgba(45, 108, 246, 0.4)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(45, 108, 246, 0.3)';
                    }}
                  >
                    {isSaving ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        {translateInterfaceText("Salvando...")}</>
                    ) : (
                      <>
                        {translateInterfaceText("Salvar Alterações")}</>
                    )}
                  </Button>
                </TooltipTrigger>
                <TooltipContent
                  side="bottom"
                  align="center"
                  className="bg-gray-900 text-white text-xs px-3 py-1.5"
                >
                  {translateInterfaceText("Salvar todas as configurações no banco de dados")}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}

          {/* Botão Copiar Código - Aparece após salvar */}
          {isSaved && !hasUnsavedChanges && videoId && (
            <TooltipProvider delayDuration={300}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    onClick={() => setShowCopyCodeModal(true)}
                    className="gap-2 h-[38px] px-[18px] rounded-xl font-semibold text-sm shadow-lg transition-all duration-200 border-0"
                    size="sm"
                    style={{
                      background: 'linear-gradient(135deg, #2D6CF6 0%, #1E5FE6 100%)',
                      color: '#FFFFFF',
                      boxShadow: '0 4px 12px rgba(45, 108, 246, 0.3)'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-1px)';
                      e.currentTarget.style.boxShadow = '0 6px 20px rgba(45, 108, 246, 0.4)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(45, 108, 246, 0.3)';
                    }}
                  >
                    <Code className="h-[18px] w-[18px]" />
                    {translateInterfaceText("Copiar código")}</Button>
                </TooltipTrigger>
                <TooltipContent
                  side="bottom"
                  align="center"
                  className="bg-gray-900 text-white text-xs px-3 py-1.5"
                >
                  {translateInterfaceText("Copiar código de incorporação do vídeo")}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}

          {showEmbedButton && onEmbedClick && (
            <TooltipProvider delayDuration={300}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    onClick={onEmbedClick}
                    className="gap-2 h-[38px] px-[18px] rounded-xl font-semibold text-sm shadow-sm transition-all duration-200 border"
                    size="sm"
                    style={{
                      background: 'rgba(45, 108, 246, 0.07)',
                      borderColor: 'rgba(45, 108, 246, 0.25)',
                      color: '#2D6CF6'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'rgba(45, 108, 246, 0.13)';
                      e.currentTarget.style.borderColor = 'rgba(45, 108, 246, 0.4)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'rgba(45, 108, 246, 0.07)';
                      e.currentTarget.style.borderColor = 'rgba(45, 108, 246, 0.25)';
                    }}
                  >
                    <Code className="h-[18px] w-[18px]" />
                    {translateInterfaceText("Copiar Embed")}</Button>
                </TooltipTrigger>
                <TooltipContent
                  side="bottom"
                  align="center"
                  className="bg-gray-900 text-white text-xs px-3 py-1.5"
                >
                  {translateInterfaceText("Copiar código de incorporação")}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          {(location.pathname === '/' || location.pathname === '/dashboard') && <NotificationsMenu recentConversions={recentConversions} recentlyReadyVideos={recentlyReadyVideos} />}
        </div>
        </div>

        {copyCodeModal}
      </header>
    );
  }

  return (
    <header className="desktop-header hidden lg:flex items-center justify-between bg-background h-[78px] pl-[24px] pr-[20px]">
      <div className="max-w-[1280px] w-full flex items-center justify-between">
        {/* Lado esquerdo: search input, logo ou espaço */}
        {onSearchChange ? (
          <div className="relative" style={{ width: 'clamp(320px, 40%, 560px)' }}>
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#9CA3AF] opacity-60 pointer-events-none" />
            <input
              type="text"
              placeholder={translateInterfaceText("Buscar vídeos ou pastas…")}
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              className="w-full h-[40px] pl-9 pr-4 text-sm rounded-[10px] border transition-all duration-[220ms] placeholder:text-[#9CA3AF]"
              style={{
                background: '#F8FAFC',
                borderColor: 'rgba(15,23,42,0.10)',
                outline: 'none',
                boxShadow: 'none',
              }}
              onFocus={(e) => {
                e.target.style.borderColor = 'rgba(59,130,246,0.55)';
                e.target.style.boxShadow = '0 0 0 3px rgba(59,130,246,0.25)';
                e.target.style.outline = 'none';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = 'rgba(15,23,42,0.10)';
                e.target.style.boxShadow = 'none';
              }}
            />
          </div>
        ) : showLogo ? (
          <button
            onClick={() => { if (location.pathname !== '/dashboard') navigate('/dashboard'); }}
            className="hover:opacity-80 transition-opacity"
            title={translateInterfaceText("Voltar ao dashboard")}
          >
            <img
              src="/assets/imgs/logo-azul-e-preta.png"
              alt={translateInterfaceText("Host VSL")}
              className="h-8"
            />
          </button>
        ) : (
          <div className="flex-1" />
        )}

        <div className="flex items-center gap-[12px] mr-[6px]">
          <ThemeToggle />
          {!location.pathname.startsWith('/personalizations') && <TourChecklist />}
          {/* Badge de alterações não salvas */}
          {hasUnsavedChanges && !isSaving && (
            <div className="flex items-center gap-2 px-3 h-[38px] rounded-xl">
              <div className="w-2 h-2 bg-orange-400 rounded-full animate-pulse" />
              <span className="text-xs font-medium text-gray-400">{translateInterfaceText("Alterações não salvas")}</span>
            </div>
          )}

          {/* Botão Salvar - Aparece quando há mudanças não salvas */}
          {hasUnsavedChanges && onSave && (
            <TooltipProvider delayDuration={300}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    onClick={onSave}
                    disabled={isSaving}
                    className="gap-2 h-[38px] px-[18px] rounded-xl font-semibold text-sm shadow-lg transition-all duration-200 border-0"
                    size="sm"
                    style={{
                      background: 'linear-gradient(135deg, #2D6CF6 0%, #1E5FE6 100%)',
                      color: '#FFFFFF',
                    }}
                  >
                    {isSaving ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        {translateInterfaceText("Salvando...")}</>
                    ) : (
                      <>
                        {translateInterfaceText("Salvar Alterações")}</>
                    )}
                  </Button>
                </TooltipTrigger>
                <TooltipContent
                  side="bottom"
                  align="center"
                  className="bg-gray-900 text-white text-xs px-3 py-1.5"
                >
                  {translateInterfaceText("Salvar todas as configurações no banco de dados")}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}

          {/* Botão Copiar Código - Aparece após salvar */}
          {isSaved && !hasUnsavedChanges && videoId && (
            <TooltipProvider delayDuration={300}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    onClick={() => setShowCopyCodeModal(true)}
                    className="gap-2 h-[38px] px-[18px] rounded-xl font-semibold text-sm shadow-lg transition-all duration-200 border-0"
                    size="sm"
                    style={{
                      background: 'linear-gradient(135deg, #2D6CF6 0%, #1E5FE6 100%)',
                      color: '#FFFFFF',
                      boxShadow: '0 4px 12px rgba(45, 108, 246, 0.3)'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-1px)';
                      e.currentTarget.style.boxShadow = '0 6px 20px rgba(45, 108, 246, 0.4)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(45, 108, 246, 0.3)';
                    }}
                  >
                    <Code className="h-[18px] w-[18px]" />
                    {translateInterfaceText("Copiar código")}</Button>
                </TooltipTrigger>
                <TooltipContent
                  side="bottom"
                  align="center"
                  className="bg-gray-900 text-white text-xs px-3 py-1.5"
                >
                  {translateInterfaceText("Copiar código de incorporação do vídeo")}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}

          {(location.pathname === '/' || location.pathname === '/dashboard') && <NotificationsMenu recentConversions={recentConversions} recentlyReadyVideos={recentlyReadyVideos} />}
        </div>
      </div>

      {copyCodeModal}
    </header>
  );
}
